import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { getSkills, getUserSkill, setUserSkill } from '@/services/skill';
import { useChatSessionsStore } from '@/stores/chatSessions';
import { useDataSourcesStore } from '@/stores/dataSourcesStore';
import { useTenantStore } from '@/stores/tenant';
import { GenerationMode, Skill, Skills } from '@/types';

export const useAgentStore = defineStore('agent', () => {
    const selectedSkill = ref<Skills>(Skills.NONE);
    const isReasoningEnabled = ref<boolean>(false);
    const isWebEnabled = ref<boolean>(false);
    const isDeepResearchEnabled = ref<boolean>(false);
    const isMyDocumentsEnabled = ref<boolean>(false);
    const isPptxAgentEnabled = ref<boolean>(false);
    const isSlideStudioAgentEnabled = ref<boolean>(false);
    const isDocStudioAgentEnabled = ref<boolean>(false);
    const isRagAgentEnabled = ref<boolean>(false);
    const isMaxModeEnabled = ref<boolean>(false);
    const reloadDeepResearchCount = ref<boolean>(false);
    const loadingSkills = ref<boolean>(false);
    const availableSkills = ref<Skill[]>([]);
    const isSidePanelChatEnabled = ref<boolean>(false);
    const route = useRoute();
    const tenantStore = useTenantStore();
    const dataSourcesStore = useDataSourcesStore();
    const chatSessionsStore = useChatSessionsStore();

    const sessionId = computed(() => {
        // Check route.params first (for typical routes)
        if (route.params.session_id) {
            return route.params.session_id as string;
        }
        // Check route.query next (for viewer routes with query parameters)
        if (
            (route.query.fileType === 'pptx' ||
                route.query.fileType === 'doc' ||
                route.query.fileType === 'docx') &&
            route.query.chatId
        ) {
            return route.query.chatId as string;
        }
        return undefined;
    });

    const isDefaultChatEnabled = computed(() => {
        return !isWebEnabled.value && !isMyDocumentsEnabled.value;
    });

    const selectedMode = computed(() => {
        if (isWebEnabled.value && isDeepResearchEnabled.value) {
            return GenerationMode.DEEP_RESEARCH;
        } else if (
            (isDefaultChatEnabled.value || isMyDocumentsEnabled.value) &&
            isReasoningEnabled.value
        ) {
            return GenerationMode.REASONING;
        } else if (isPptxAgentEnabled.value) {
            return GenerationMode.PPTX_AGENT;
        } else if (isSlideStudioAgentEnabled.value) {
            return GenerationMode.SLIDE_STUDIO_AGENT;
        } else if (isDocStudioAgentEnabled.value) {
            return GenerationMode.DOC_STUDIO_AGENT;
        } else if (isRagAgentEnabled.value && isMyDocumentsEnabled.value) {
            return GenerationMode.RAG_AGENT;
        }
        return GenerationMode.DEFAULT;
    });

    const setSelectedSkill = (skill: Skills) => {
        selectedSkill.value = skill;
        if (skill !== Skills.CHAT_WITH_WEB) {
            isDeepResearchEnabled.value = false;
        }
        if (skill !== Skills.CHAT_WITH_LLM) {
            isReasoningEnabled.value = false;
        }
        if (skill !== Skills.CHAT_WITH_DOCUMENTS) {
            isRagAgentEnabled.value = false;
        }
    };

    const loadSkills = async (tenantId: string) => {
        if (!tenantId) return;

        setLoadingSkills(true);
        try {
            const { skills } = await getSkills(tenantId);
            availableSkills.value = skills;
        } catch (error) {
            console.error('Error loading skills:', error);
        } finally {
            setLoadingSkills(false);
        }
    };

    const loadUserSkill = async (tenantId: string, sessionId: string) => {
        if (!tenantId || !sessionId) return;

        setLoadingSkills(true);
        try {
            const skill = await getUserSkill(tenantId, sessionId);
            if (skill === null) {
                setSelectedSkill(Skills.CHAT_WITH_LLM);
            } else {
                setSelectedSkill(skill as Skills);
            }
        } catch (error) {
            console.error('Error loading user skill:', error);
        } finally {
            setLoadingSkills(false);
        }
    };

    watch(
        () => isWebEnabled.value,
        (newValue) => {
            if (newValue) {
                isMyDocumentsEnabled.value = false;
                // Don't disable PPTX agent if we're in side panel mode
                if (!isSidePanelChatEnabled.value) {
                    isPptxAgentEnabled.value = false;
                }
                isRagAgentEnabled.value = false;
                setSelectedSkill(Skills.CHAT_WITH_WEB);
            } else if (!isMyDocumentsEnabled.value) {
                setSelectedSkill(Skills.CHAT_WITH_LLM);
            }
        }
    );

    watch(
        () => isMyDocumentsEnabled.value,
        (newValue) => {
            if (newValue) {
                isWebEnabled.value = false;
                setSelectedSkill(Skills.CHAT_WITH_DOCUMENTS);
            } else if (!isWebEnabled.value) {
                setSelectedSkill(Skills.CHAT_WITH_LLM);
                // Don't disable PPTX agent if we're in side panel mode
                if (!isSidePanelChatEnabled.value) {
                    isPptxAgentEnabled.value = false;
                }
                isRagAgentEnabled.value = false;
            }
        }
    );

    watch(
        () => isPptxAgentEnabled.value,
        (newValue) => {
            if (newValue) {
                isDeepResearchEnabled.value = false;
                isReasoningEnabled.value = false;
                isRagAgentEnabled.value = false;
                isSlideStudioAgentEnabled.value = false;
                isDocStudioAgentEnabled.value = false;
            }
        }
    );
    watch(
        () => isSlideStudioAgentEnabled.value,
        (newValue) => {
            if (newValue) {
                isDeepResearchEnabled.value = false;
                isReasoningEnabled.value = false;
                isRagAgentEnabled.value = false;
                isPptxAgentEnabled.value = false;
                isDocStudioAgentEnabled.value = false;
            }
        }
    );
    watch(
        () => isDocStudioAgentEnabled.value,
        (newValue) => {
            if (newValue) {
                isDeepResearchEnabled.value = false;
                isReasoningEnabled.value = false;
                isRagAgentEnabled.value = false;
                isPptxAgentEnabled.value = false;
                isSlideStudioAgentEnabled.value = false;
            }
        }
    );
    watch(selectedMode, (newMode) => {
        if (newMode === GenerationMode.PPTX_AGENT) {
            isPptxAgentEnabled.value = true;
        } else if (newMode === GenerationMode.SLIDE_STUDIO_AGENT) {
            isSlideStudioAgentEnabled.value = true;
        } else if (newMode === GenerationMode.DOC_STUDIO_AGENT) {
            isDocStudioAgentEnabled.value = true;
        } else if (newMode === GenerationMode.RAG_AGENT) {
            isRagAgentEnabled.value = true;
        } else {
            if (!isSidePanelChatEnabled.value) {
                isPptxAgentEnabled.value = false;
                isSlideStudioAgentEnabled.value = false;
                isDocStudioAgentEnabled.value = false;
            }
            isRagAgentEnabled.value = false;
        }
    });

    // Watch for changes to selectedSkill and persist to the backend
    watch(
        () => selectedSkill.value,
        async (newSkillName, oldSkillName) => {
            if (
                newSkillName === oldSkillName ||
                chatSessionsStore.isLoadingChatSession
            ) {
                return;
            }

            if (sessionId.value && tenantStore.tenantId) {
                await setUserSkill(
                    tenantStore.tenantId,
                    newSkillName,
                    sessionId.value
                );
            }
        }
    );

    // Combined watch for skill selection and document attachment
    watch(
        [
            () => selectedSkill.value,
            () => dataSourcesStore.selectedAttachedFiles,
            () => dataSourcesStore.selectedDatasources,
            () => chatSessionsStore.isLoadingChatSession
        ],
        ([newSkill]) => {
            // Wait for the chat session to be loaded before updating the skill
            if (chatSessionsStore.isLoadingChatSession) {
                return;
            }
            // First, update UI flags based on current skill
            if (newSkill === Skills.CHAT_WITH_WEB) {
                isWebEnabled.value = true;
                return; // If web is selected, don't change the skill regardless of documents
            } else if (newSkill === Skills.CHAT_WITH_DOCUMENTS) {
                isMyDocumentsEnabled.value = true;
            } else {
                isWebEnabled.value = false;
                isMyDocumentsEnabled.value = false;
            }
            // Then, handle automatic skill switching based on document presence
            // Only if we're not already in web mode
            if (dataSourcesStore.shouldUseChatWithDocuments) {
                isMyDocumentsEnabled.value = true;
                setSelectedSkill(Skills.CHAT_WITH_DOCUMENTS);
            } else {
                isMyDocumentsEnabled.value = false;
                setSelectedSkill(Skills.CHAT_WITH_LLM);
            }
        }
    );

    const setLoadingSkills = (loading: boolean) => {
        loadingSkills.value = loading;
    };

    const setSidePanelChatEnabled = (enabled: boolean) => {
        isSidePanelChatEnabled.value = enabled;
    };

    return {
        isReasoningEnabled,
        isWebEnabled,
        isDeepResearchEnabled,
        isPptxAgentEnabled,
        isSlideStudioAgentEnabled,
        isDocStudioAgentEnabled,
        isRagAgentEnabled,
        selectedMode,
        isMyDocumentsEnabled,
        isDefaultChatEnabled,
        isMaxModeEnabled,
        selectedSkill,
        setSelectedSkill,
        reloadDeepResearchCount,
        loadingSkills,
        loadSkills,
        availableSkills,
        loadUserSkill,
        isSidePanelChatEnabled,
        setSidePanelChatEnabled
    };
});
